import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { geocodeAddress } from "@/lib/map-utils";
import { formatPhoneNumber } from "@/lib/phoneUtils";

interface AddLocationSheetProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: Id<"businesses">;
}

export function AddLocationSheet({ isOpen, onClose, businessId }: AddLocationSheetProps) {
  const [serviceDays, setServiceDays] = useState<string[]>([
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
  ]);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    squareFootage: "",
    staffRequired: "",
    cleaningFrequency: "5_per_week",
    contactName: "",
    contactTitle: "",
    contactEmail: "",
    contactPhone: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Convex mutation
  const createLocation = useMutation(api.serviceLocations.create);

  const handleInputChange = (field: string, value: string) => {
    // Apply phone formatting for phone fields
    const formattedValue = field === "contactPhone" ? formatPhoneNumber(value) : value;
    setFormData((prev) => ({
      ...prev,
      [field]: formattedValue,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.address || !formData.squareFootage) {
      return; // Basic validation
    }

    setIsSubmitting(true);

    try {
      // Geocode the address to get coordinates
      const coordinates = await geocodeAddress(formData.address);

      await createLocation({
        name: formData.name,
        address: formData.address,
        businessId: businessId,
        squareFootage: formData.squareFootage,
        cleaningFrequency: formData.cleaningFrequency,
        serviceDays: serviceDays,
        staffRequired: formData.staffRequired ? parseInt(formData.staffRequired) : undefined,
        coordinates: coordinates || undefined,
        contactName: formData.contactName || undefined,
        contactTitle: formData.contactTitle || undefined,
        contactEmail: formData.contactEmail || undefined,
        contactPhone: formData.contactPhone || undefined,
      });

      // Reset form
      setFormData({
        name: "",
        address: "",
        squareFootage: "",
        staffRequired: "",
        cleaningFrequency: "5_per_week",
        contactName: "",
        contactTitle: "",
        contactEmail: "",
        contactPhone: "",
      });
      setServiceDays(["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]);

      onClose();
    } catch (error) {
      console.error("Failed to create location:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[500px] sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>Add Service Location</SheetTitle>
          <SheetDescription>Enter the details for the new service location.</SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 px-2 py-6">
            <div className="space-y-2">
              <Label htmlFor="name">Location Name</Label>
              <Input
                id="name"
                placeholder="e.g. Main Office, Warehouse"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                placeholder="Full address"
                value={formData.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="square-footage">Square Footage</Label>
                <Input
                  id="square-footage"
                  placeholder="e.g. 25000"
                  value={formData.squareFootage}
                  onChange={(e) => handleInputChange("squareFootage", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="staff">Staff Required</Label>
                <Input
                  id="staff"
                  type="number"
                  placeholder="Number of staff"
                  value={formData.staffRequired}
                  onChange={(e) => handleInputChange("staffRequired", e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="frequency">Service Frequency</Label>
              <Select
                value={formData.cleaningFrequency}
                onValueChange={(value) => handleInputChange("cleaningFrequency", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1_per_week">1 time per week</SelectItem>
                  <SelectItem value="2_per_week">2 times per week</SelectItem>
                  <SelectItem value="3_per_week">3 times per week</SelectItem>
                  <SelectItem value="4_per_week">4 times per week</SelectItem>
                  <SelectItem value="5_per_week">5 times per week</SelectItem>
                  <SelectItem value="6_per_week">6 times per week</SelectItem>
                  <SelectItem value="7_per_week">7 times per week</SelectItem>
                  <SelectItem value="1_per_month">1 time per month</SelectItem>
                  <SelectItem value="2_per_month">2 times per month</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="serviceDays">Service Days</Label>
              <div className="flex flex-wrap gap-2">
                {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map(
                  (day) => (
                    <button
                      key={day}
                      type="button"
                      className={`rounded-full border px-3 py-1.5 text-sm transition-all ${
                        serviceDays.includes(day)
                          ? "border-blue-500 bg-blue-500 text-white"
                          : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                      }`}
                      onClick={() => {
                        const newDays = serviceDays.includes(day)
                          ? serviceDays.filter((d) => d !== day)
                          : [...serviceDays, day];
                        setServiceDays(newDays);
                      }}
                    >
                      {day.substring(0, 3)}
                    </button>
                  )
                )}
              </div>
            </div>

            <div className="space-y-4 border-t pt-4">
              <h3 className="text-sm font-medium">Contact Information</h3>

              <div className="space-y-2">
                <Label htmlFor="contactName">Contact Name</Label>
                <Input
                  id="contactName"
                  placeholder="e.g. John Smith"
                  value={formData.contactName}
                  onChange={(e) => handleInputChange("contactName", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactTitle">Contact Title</Label>
                <Input
                  id="contactTitle"
                  placeholder="e.g. Site Manager, Facility Manager"
                  value={formData.contactTitle}
                  onChange={(e) => handleInputChange("contactTitle", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactEmail">Contact Email</Label>
                <Input
                  id="contactEmail"
                  type="email"
                  placeholder="e.g. contact@example.com"
                  value={formData.contactEmail}
                  onChange={(e) => handleInputChange("contactEmail", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactPhone">Contact Phone</Label>
                <Input
                  id="contactPhone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.contactPhone}
                  onChange={(e) => handleInputChange("contactPhone", e.target.value)}
                  maxLength={14}
                />
              </div>
            </div>

            <Button
              className="w-full"
              onClick={handleSubmit}
              disabled={
                isSubmitting || !formData.name || !formData.address || !formData.squareFootage
              }
            >
              {isSubmitting ? "Adding Location..." : "Add Location"}
            </Button>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
